import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { PushToggle } from "@/components/PushToggle";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LogOut, Home, Bell } from "lucide-react";

export const DashboardShell = ({ title, subtitle, tabs, active, onTab, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [showN, setShowN] = useState(false);

  const loadNotifs = async () => { try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {} };
  useEffect(() => { loadNotifs(); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="sticky top-0 z-40 glass border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 flex h-[70px] items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center gap-1.5 text-sm text-navy/70 hover:text-navy" data-testid="back-home"><Home className="h-4 w-4" /> Site</Link>
            <PushToggle />
            <div className="relative">
              <button onClick={() => { setShowN((v) => !v); }} className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-navy/10 text-navy hover:bg-navy/5" data-testid="notif-bell">
                <Bell className="h-5 w-5" />
                {unread > 0 && <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-[10px] font-bold text-navy">{unread}</span>}
              </button>
              {showN && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-xl border bg-white p-2 shadow-xl" onMouseLeave={() => setShowN(false)}>
                  <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">Notifications</div>
                  {notifs.length === 0 ? <p className="px-2 py-4 text-sm text-slate-400">Aucune notification.</p> :
                    notifs.slice(0, 10).map((n) => (
                      <div key={n.id} className={`rounded-lg px-3 py-2.5 text-sm ${n.read ? "text-slate-500" : "bg-gold/10 text-navy"}`} data-testid={`notif-${n.id}`}>
                        <div className="font-medium">{n.title}</div>
                        <div className="text-xs opacity-70">{n.message}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="hidden sm:flex flex-col items-end leading-tight mr-1">
              <span className="text-sm font-semibold text-navy">{user?.name}</span>
              <span className="text-[11px] uppercase tracking-widest text-gold">{user?.role}</span>
            </div>
            <button onClick={doLogout} className="flex h-10 w-10 items-center justify-center rounded-lg border border-navy/10 text-red-600 hover:bg-red-50" data-testid="dash-logout"><LogOut className="h-5 w-5" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-3xl font-bold text-navy">{title}</h1>
          {subtitle && <p className="text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {tabs && (
          <div className="flex flex-wrap gap-2 mb-8 border-b border-black/5 pb-3">
            {tabs.map((t) => (
              <button key={t.value} onClick={() => onTab(t.value)} data-testid={`tab-${t.value}`}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${active === t.value ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export const KpiCard = ({ label, value, accent, testid }) => (
  <div className="rounded-xl bg-white border border-black/5 p-6" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className={`mt-2 font-mono text-3xl font-bold ${accent ? "text-gold" : "text-navy"}`}>{value}</div>
  </div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    "Nouvelle": "bg-blue-100 text-blue-700", "En traitement": "bg-amber-100 text-amber-700",
    "En attente": "bg-purple-100 text-purple-700", "Terminée": "bg-emerald-100 text-emerald-700",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
};
